import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import swal from 'sweetalert';
import { lookupGroupDeparture, reserveGroupDeparture } from '../../api/groupDepartureApi';
import { loadBookableJourneysPage, filterGroupBookableJourneys } from '../../utils/bookableJourneyLoader';
import { buildBookableMonthOptions, fetchBookingHorizon } from '../../utils/bookingAvailability';
import { PaymentMethodModal } from '../payments';
import { formatMoneyLegacy } from '../../utils/currency';
import getDepartureCardCopy from '../../utils/getDepartureCardCopy';
import DeparturePricingSummary from './DeparturePricingSummary';

function GroupDepartureReserveFlow({ initialJourneySlug, initialYearMonth }) {
	const location = useLocation();
	const history = useHistory();
	const currentUser = useSelector((state) => state.userStore?.currentUser) || {};
	const params = new URLSearchParams(location.search);
	const initialJourney = initialJourneySlug || params.get('journey') || 'everest-base-camp';
	const initialMonth = initialYearMonth || params.get('month') || '';

	const [journeySlug, setJourneySlug] = useState(initialJourney);
	const [yearMonth, setYearMonth] = useState(initialMonth);
	const [departure, setDeparture] = useState(null);
	const [monthBookable, setMonthBookable] = useState(true);
	const [lookupLoading, setLookupLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [paymentModalData, setPaymentModalData] = useState(null);
	const [form, setForm] = useState({
		name: '',
		phone: '',
		number_of_people: 1,
	});

	const [groupJourneys, setGroupJourneys] = useState([]);
	const [bookingHorizon, setBookingHorizon] = useState(12);

	const selectedJourney = groupJourneys.find((j) => j.slug === journeySlug) || null;
	const monthOpts = useMemo(
		() => (selectedJourney ? buildBookableMonthOptions(selectedJourney, bookingHorizon) : []),
		[selectedJourney, bookingHorizon]
	);
	const journeyTitle = selectedJourney?.title?.replace(/\s+Journey$/i, '') || journeySlug;
	const maxTravellers = departure?.spots_left || departure?.max_group_size || 12;
	const travellerCount = Math.max(1, Math.min(maxTravellers, Number(form.number_of_people) || 1));

	useEffect(() => {
		fetchBookingHorizon().then(setBookingHorizon);
		loadBookableJourneysPage({ per_page: 200, group_enabled: true }).then(({ journeys: list }) => {
			setGroupJourneys(filterGroupBookableJourneys(list));
		});
	}, []);

	useEffect(() => {
		if (!monthOpts.length) return;
		if (!monthOpts.some((m) => m.value === yearMonth)) {
			setYearMonth(monthOpts[0].value);
		}
	}, [journeySlug, monthOpts, yearMonth]);

	useEffect(() => {
		const p = new URLSearchParams(location.search);
		if (p.get('pay_rest') || p.get('pay_interest')) return;
		const j = p.get('journey');
		const m = p.get('month');
		if (j && j !== journeySlug) setJourneySlug(j);
		if (m && m !== yearMonth) setYearMonth(m);
	}, [location.search]);

	useEffect(() => {
		const p = new URLSearchParams(location.search);
		if (p.get('pay_rest') || p.get('pay_interest')) return;
		if (!yearMonth) return;
		if (p.get('journey') === journeySlug && p.get('month') === yearMonth) return;
		p.set('journey', journeySlug);
		p.set('month', yearMonth);
		history.replace({ pathname: '/group-trips', search: p.toString() });
	}, [journeySlug, yearMonth, history, location.search]);

	useEffect(() => {
		if (currentUser?.name || currentUser?.first_name) {
			setForm((f) => ({
				...f,
				name: f.name || currentUser.name || [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' '),
				phone: f.phone || currentUser.phone || currentUser.contact_number || '',
			}));
		}
	}, [currentUser]);

	useEffect(() => {
		if (!journeySlug || !yearMonth) return;
		setLookupLoading(true);
		lookupGroupDeparture({ journey_slug: journeySlug, year_month: yearMonth })
			.then((res) => {
				setDeparture(res.data?.group_departure || null);
				setMonthBookable(res.data?.month_bookable !== false);
			})
			.catch(() => {
				setDeparture(null);
				setMonthBookable(true);
			})
			.finally(() => setLookupLoading(false));
	}, [journeySlug, yearMonth]);

	const statusMeta = () => {
		if (lookupLoading) return { tone: 'loading' };
		if (!monthBookable) {
			return {
				tone: 'warn',
				copy: {
					kind: 'closed',
					statusLabel: 'Registration for this month has closed',
					guaranteeMessage: monthOpts.length
						? 'Choose another month from the list above.'
						: 'No departure months are currently open for this journey.',
				},
			};
		}
		if (!departure) return { tone: 'loading' };
		return { tone: 'ready', copy: getDepartureCardCopy(departure) };
	};

	const handleReserve = async (e) => {
		e.preventDefault();
		if (!(currentUser && (currentUser.idx || currentUser.email))) {
			const returnTo = `${location.pathname}?journey=${encodeURIComponent(journeySlug)}&month=${encodeURIComponent(yearMonth)}`;
			window.location.href = `/login?return=${encodeURIComponent(returnTo)}`;
			return;
		}
		if (!form.name.trim() || !form.phone.trim()) {
			swal('Missing details', 'Please enter your name and phone number.', 'warning');
			return;
		}
		setSubmitting(true);
		try {
			const res = await reserveGroupDeparture({
				journey_slug: journeySlug,
				journey_title: journeyTitle,
				year_month: yearMonth,
				name: form.name.trim(),
				phone: form.phone.trim(),
				number_of_people: travellerCount,
			});
			const payment = res.data?.payment;
			if (payment?.idx) {
				setPaymentModalData({
					productIdentity: payment.idx,
					productName: 'GROUP_TOURISM',
					productUrl: `${window.location.origin}/group-trips`,
					amount: Number(payment.amount),
					currencyCode: departure?.currency_code || 'USD',
				});
				setShowPaymentModal(true);
			}
			if (res.data?.group_tour) setDeparture(res.data.group_tour);
		} catch (err) {
			const msg = err.response?.data?.errors?.[0] || 'Could not reserve your place. Please try again.';
			swal('Reservation failed', msg, 'error');
		} finally {
			setSubmitting(false);
		}
	};

	const adjustTravellers = (delta) => {
		const next = Math.max(1, Math.min(maxTravellers, travellerCount + delta));
		setForm({ ...form, number_of_people: next });
	};

	const currencyCode = departure?.currency_code || 'USD';
	const perPersonDeposit = departure?.interest_amount ? Number(departure.interest_amount) : null;
	const totalDeposit = perPersonDeposit ? perPersonDeposit * travellerCount : null;
	const totalDepositDisplay = totalDeposit ? formatMoneyLegacy(totalDeposit, currencyCode) : null;
	const status = statusMeta();
	const ctaLabel = submitting
		? 'Reserving…'
		: totalDepositDisplay
			? `Reserve · ${totalDepositDisplay} deposit`
			: 'Reserve your seat';

	return (
		<div className="booking-form-panel gd-booking-panel" id="gd-reserve-anchor">
			<header className="booking-form-panel__head gd-booking-panel__head">
				<h2 className="booking-form-panel__title gd-booking-panel__title">Reserve your seat</h2>
			</header>

			<form className="gd-booking-form" onSubmit={handleReserve} noValidate>
				<div className="gd-booking-form__trip">
					<div className="gd-booking-form__grid gd-booking-form__grid--trip">
						<label className="gd-booking-form__field">
							<span className="gd-booking-form__label">Journey</span>
							<select value={journeySlug} onChange={(e) => setJourneySlug(e.target.value)}>
								{groupJourneys.map((j) => (
									<option key={j.slug} value={j.slug}>{j.title.replace(/\s+Journey$/i, '')}</option>
								))}
							</select>
						</label>
						<label className="gd-booking-form__field">
							<span className="gd-booking-form__label">Departure month</span>
							<select value={yearMonth || ''} onChange={(e) => setYearMonth(e.target.value)} disabled={!monthOpts.length}>
								{!monthOpts.length && <option value="">No months open</option>}
								{monthOpts.map((m) => (
									<option key={m.value} value={m.value}>{m.label}</option>
								))}
							</select>
						</label>
						<div className="gd-booking-form__field gd-booking-form__field--travellers">
							<span className="gd-booking-form__label" id="gd-travellers-label">Travellers</span>
							<div className="gd-booking-form__stepper" role="group" aria-labelledby="gd-travellers-label">
								<button
									type="button"
									className="gd-booking-form__stepper-btn"
									onClick={() => adjustTravellers(-1)}
									disabled={travellerCount <= 1}
									aria-label="Fewer travellers"
								>
									−
								</button>
								<input
									type="number"
									min="1"
									max={maxTravellers}
									value={travellerCount}
									onChange={(e) => setForm({ ...form, number_of_people: e.target.value })}
									aria-label="Number of travellers"
								/>
								<button
									type="button"
									className="gd-booking-form__stepper-btn"
									onClick={() => adjustTravellers(1)}
									disabled={travellerCount >= maxTravellers}
									aria-label="More travellers"
								>
									+
								</button>
							</div>
						</div>
					</div>
					{selectedJourney && (
						<p className="gd-booking-form__trip-hint">
							{monthOpts.length
								? `Available up to ${bookingHorizon} months ahead for this journey.`
								: 'No departure months are currently open for this journey.'}
						</p>
					)}
				</div>

				{status.tone === 'loading' && (
					<p className="gd-booking-form__status gd-booking-form__status--loading" role="status" aria-live="polite">
						Checking availability…
					</p>
				)}
				{status.tone !== 'loading' && status.copy && (
					<p
						className={`gd-booking-form__status gd-booking-form__status--${status.copy.kind}`}
						role="status"
						aria-live="polite"
					>
						<strong>{status.copy.statusLabel}</strong>
						<span>{status.copy.guaranteeMessage}</span>
					</p>
				)}

				{!lookupLoading && monthBookable && departure && (
					<DeparturePricingSummary
						departure={departure}
						travellerCount={travellerCount}
						className="gd-booking-form__pricing"
					/>
				)}

				<div className="gd-booking-form__grid gd-booking-form__grid--contact">
					<label className="gd-booking-form__field">
						<span className="gd-booking-form__label">Full name</span>
						<input
							type="text"
							value={form.name}
							onChange={(e) => setForm({ ...form, name: e.target.value })}
							placeholder="As on passport"
							autoComplete="name"
							required
						/>
					</label>
					<label className="gd-booking-form__field">
						<span className="gd-booking-form__label">Phone / WhatsApp</span>
						<input
							type="tel"
							value={form.phone}
							onChange={(e) => setForm({ ...form, phone: e.target.value })}
							placeholder="+977 98XXXXXXXX"
							autoComplete="tel"
							required
						/>
					</label>
				</div>

				<div className="gd-booking-form__footer">
					<button
						type="submit"
						className="gd-booking-form__cta"
						disabled={submitting || lookupLoading || !monthBookable || !monthOpts.length}
					>
						{ctaLabel}
					</button>
					{travellerCount > 1 && totalDepositDisplay && (
						<p className="gd-booking-form__fine">{totalDepositDisplay} total deposit for {travellerCount} travellers</p>
					)}
				</div>
			</form>

			<PaymentMethodModal
				open={showPaymentModal}
				onClose={() => { setShowPaymentModal(false); setPaymentModalData(null); }}
				paymentParams={paymentModalData}
				amount={paymentModalData?.amount}
			/>
		</div>
	);
}

export default GroupDepartureReserveFlow;
